import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import React, { useEffect, useState } from "react";
import { Loader, Text, Group, ColorSwatch, Anchor } from "@mantine/core"
import { getAbstract } from "../../services/evidence";
import CustomHighlight from "./CustomHighlight/CustomHighlight";
import { getHardcodedColor, getHardcodedLabels } from "../../utils";
import { nodeTypes, txtminingFields } from "../../types";

const PubTable = ({ data, height }) => {
    const [expanded, setExpanded] = useState({});
    const [rowData, setRowData] = useState({});

    const columns = [
        {
            accessorKey: "url",
            header: "Publication URL",
            enableSorting: false,
            enableColumnFilter: false,
            enableColumnActions: false,
            enableHiding: false,
            Cell: ({ cell }) => <Anchor href={cell.row.original.url} target="_blank" rel="noopener noreferrer" underline="hover">{cell.row.original.id}</Anchor>
        },
        {
            accessorKey: "year",
            header: "Year",
            enableSorting: true,
            enableColumnFilter: true,
            enableColumnActions: false,
            enableHiding: false
        },
    ]

    useEffect(() => {
        const expandedRowId = Object.keys(expanded).find(
            (rowId) => expanded[rowId] && rowData[rowId] === undefined
        );
        if (!expandedRowId) return;

        setRowData((prev) => ({
            ...prev,
            [expandedRowId]: 'loading',
        }));

        const callGetAbstract = async () => {
            try {
                const response = await getAbstract(expandedRowId);
                setRowData((prev) => ({
                    ...prev,
                    [expandedRowId]: response.abstract,
                }));
            } catch (error) {
                setRowData((prev) => ({
                    ...prev,
                    [expandedRowId]: { error: error.message },
                }));
            }
        };

        callGetAbstract();
    }, [JSON.stringify(Object.keys(expanded).filter((rowId) => expanded[rowId]))]);

    const table = useMantineReactTable({
        columns, data: data,
        getRowId: (row) => row.id.toString(),
        enableExpanding: true,
        enableExpandAll: false,
        enableColumnResizing: false,
        enableStickyHeader: false,
        enableGlobalFilter: false,
        enableDensityToggle: false,
        enableHiding: false,
        state: { expanded },
        mantineTableContainerProps: {
            style: {
                maxHeight: `${height * 0.7}px`, // Set table height
                overflowY: "auto"   // Enable vertical scrolling inside table
            }
        },
        mantineTableProps: {
            style: {
                tableLayout: "fixed", // Prevents content from overflowing
                width: "100%",
            }
        },
        initialState: {
            showColumnFilters: true,
            density: 'xs',
            sorting: [
                {
                    id: 'year',
                    desc: true
                }
            ]
        },
        onExpandedChange: setExpanded,
        renderDetailPanel: ({ row }) => (
            <div style={{
                padding: "10px", maxWidth: "100%",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word", maxHeight: "200px",
                overflowY: "auto"
            }}>
                <Group>
                    <Group>
                        <ColorSwatch size={10} color={getHardcodedColor(nodeTypes.gene)} />
                        <Text size="sm" c='dimmed'>{getHardcodedLabels(nodeTypes.gene)}</Text>
                    </Group>
                    <Group>
                        <ColorSwatch size={10} color={getHardcodedColor(nodeTypes.disease)} />
                        <Text size="sm" c='dimmed'>{getHardcodedLabels(nodeTypes.disease)}</Text>
                    </Group>
                    <Group>
                        <ColorSwatch size={10} color={getHardcodedColor(txtminingFields.hit)} />
                        <Text size="sm" c='dimmed'>{getHardcodedLabels(txtminingFields.hit)}</Text>
                    </Group>
                    <Group>
                        <ColorSwatch size={10} color={getHardcodedColor(txtminingFields.relation)} />
                        <Text size="sm" c='dimmed'>{getHardcodedLabels(txtminingFields.relation)}</Text>
                    </Group>
                    <Group>
                        <ColorSwatch size={10} color={getHardcodedColor(txtminingFields.mutation)} />
                        <Text size="sm" c='dimmed'>{getHardcodedLabels(txtminingFields.mutation)}</Text>
                    </Group>
                </Group>
                {rowData[row.id] && rowData[row.id] !== 'loading' ? (
                    rowData[row.id].error ? (
                        <span style={{ color: "red" }}>{rowData[row.id].error}</span>
                    ) : (
                        <CustomHighlight
                            text={(() => { return rowData[row.id].replace(/<\/?[^>]+>/g, '')})()}
                            highlight={[
                                { terms: row.original.hits? row.original.hits.split('|') : [], colors: [getHardcodedColor(txtminingFields.hit)] },
                                { terms: row.original.gene_matches? row.original.gene_matches.split('|') : [], colors: [getHardcodedColor(nodeTypes.gene)] },
                                { terms: row.original.disease_matches? row.original.disease_matches.split('|') : [], colors: [getHardcodedColor(nodeTypes.disease)] },
                                { terms: row.original.relation? row.original.relation.split('|') : [], colors: [getHardcodedColor(txtminingFields.relation)] },
                                { terms: row.original.mutation? row.original.mutation.split('|') : [], colors: [getHardcodedColor(txtminingFields.mutation)] }
                            ].filter(({ terms }) => terms.length)} />
                    )
                ) : (
                    <Loader size="sm" />
                )}
            </div>
        ),
    })

    return (
        <MantineReactTable table={table} />
    );
}

export default PubTable;